import { logger } from '@/lib/logger/';
import { getTimeToInteract } from './cplHelperFunctions';
import { LANDING_PAGE } from '@/components/Landing/constants';
import { getParams } from '../utils/params';

export const PAGE = {
  LANDING: 'billing',
};

export const EVENTS = {
  INITIAL_DATA_FETCHED: 'INITIAL_DATA_FETCHED',
  LANDING_PAGE_RENDER: 'LANDING_PAGE_RENDER',
  LANDING_USER_INTERACTION: 'LANDING_USER_INTERACTION_TIME',
  LAZY_COMPONENTS_LOADED: 'LAZY_COMPONENTS_LOADED',
  PROCESS_APPROVE: 'PROCESS_APPROVE',
  REVIEW_PAGE_RENDER: 'REVIEW_PAGE_RENDER',
  THREE_DS_CONTINGENCY: '3DS_CONTINGENCY_HANDLER',
  USER_INTERACTION: 'USER_INTERACTION_TIME',
};

const logPerformanceTimes: any = {};

const store = {
  events: {
    [EVENTS.INITIAL_DATA_FETCHED]: false,
    [EVENTS.LAZY_COMPONENTS_LOADED]: false,
  },
  hasContingencies: false,
  triggerInitialCPLEvent: true,
  triggerInitialLandingCPLEvent: true,
};

// interface for each metric inside of the comp obj
// each metric consists of a starting time and a time taken(tt)
// when it's finished
interface CompLogging {
  start: number;
  tt: number;
}

// interface for object window.cplPhases.comp.
// this object is used to record several metrics of cpl
interface WindowComp {
  SERVER_INITIAL_DATA: CompLogging | undefined;
  visibilitychange: CompLogging | undefined;
  server_side_redirect_time: CompLogging | undefined;
}

/**
 * Populate server side redirect time tracking time from xorouternode web
 * to weasley render, metrics gets added to comp object
 * ssrt - Server Side Redirection Time
 * @param {WindowComp} comp
 */
const populateServerSideRedirectMetrics = (comp?: WindowComp) => {
  const params = getParams();
  if (params?.ssrt) {
    const ssrtParam = params.ssrt as string;
    const isValidSSRT = !Number.isNaN(ssrtParam);

    if (comp && isValidSSRT) {
      // if its a number
      const ssrt = parseInt(ssrtParam);
      const endTime = Date.now(); // while change to render time
      const timeTaken = endTime - ssrt;
      // ms for one day: 3600s * 24h * 1000ms
      const oneDayMetricThreshold = 86400 * 1000;

      if (timeTaken < oneDayMetricThreshold) {
        comp.server_side_redirect_time = {
          start: ssrt,
          tt: timeTaken,
        };
      }
    }
  }
};

export const triggerCPLLatencyMetricsFPTI = (
  page: string,
  timeToInteract: number,
  transition: string,
): void => {
  // @ts-expect-error type window
  const { comp, query, chunk, ...rest } = window?.cplPhases || {};
  populateServerSideRedirectMetrics(comp);
  const cplLatencyMetrics = {
    application_name: 'weasley',
    component: 'checkoutuinodeweb',
    // @ts-expect-error type window
    cpl_chunk_metrics: JSON.stringify(window?.cplPhases?.chunk || {}),
    // @ts-expect-error type window
    cpl_comp_metrics: JSON.stringify(window?.cplPhases?.comp || {}),
    // @ts-expect-error type window
    cpl_query_metrics: JSON.stringify(window?.cplPhases?.query || {}),
    page_name: `main:xo:lite:weasley:guestAndSignup:custom-${page}`,
    state_name: 'CPL_LATENCY_METRICS',
    transition_name: transition,
    ...rest,
    total_page_load_time: timeToInteract,
  };

  logger.fpti('CPL_LATENCY_METRICS', cplLatencyMetrics);
};

export const updateEvents = (event = '', state: boolean): boolean =>
  (store.events[event] = state);

export const updateTriggerInitialCPLEvent = (
  state: boolean,
  page?: string,
): void => {
  // Handle landing page separately from billing page
  if (page === LANDING_PAGE) {
    logger.info(`CPL_UPDATE_TRIGGER_INITIAL_LANDING_CPL_EVENT_TO_${state}`);
    store.triggerInitialLandingCPLEvent = state;
  } else {
    logger.info(`CPL_UPDATE_TRIGGER_INITIAL_CPL_EVENT_TO_${state}`);
    store.triggerInitialCPLEvent = state;
  }
};

export const shouldTriggerInitialCPLEvent = (page?: string): boolean => {
  // INITITIAL_DATA will not be fetched on landing page
  if (page === LANDING_PAGE) {
    return (
      store.triggerInitialLandingCPLEvent &&
      store.events[EVENTS.LAZY_COMPONENTS_LOADED]
    );
  }

  return (
    store.triggerInitialCPLEvent &&
    store.events[EVENTS.INITIAL_DATA_FETCHED] &&
    store.events[EVENTS.LAZY_COMPONENTS_LOADED]
  );
};

export const updateHasContingencies = (state: boolean): void => {
  logger.info(`CPL_UPDATE_HAS_CONTINGENCIES_TO_${state}`);
  store.hasContingencies = state;
};

export const hasContingencies = (): boolean => {
  return store.hasContingencies;
};

export const triggerInitialCPLEvent = (page = '', event = ''): void => {
  if (event === EVENTS.REVIEW_PAGE_RENDER) {
    updateEvents(EVENTS.INITIAL_DATA_FETCHED, true);
  } else {
    updateEvents(event, true);
  }
  if (shouldTriggerInitialCPLEvent(page)) {
    if (hasContingencies()) {
      logger.info(
        `CPL_DISABLE_CALL_BECAUSE_OF_CONTINGENCIES_FROM_${page}_PAGE`,
      );
    } else {
      const timeToInteract = getTimeToInteract();
      logger.cpl.logPerformance({
        action: `${page}_page_loaded`,
        page: `main:xo:lite:weasley:guestAndSignup:custom-${page}`,
        timeToInteract,
        uicomp: `custom-${page}`,
        uitype: 'page',
      });
      logger.info(`CPL_CALL_TRIGGERED_FROM_${page}_PAGE`);

      // Set the flag to false so that CPL call is not triggered again
      updateTriggerInitialCPLEvent(false, page);
      // Trigger FPTI event to capture the time spent on different phases.
      triggerCPLLatencyMetricsFPTI(page, timeToInteract, event);
    }
  }
};

export const resetLogPerformanceTime = (logPerformanceName: string): void => {
  logPerformanceTimes[logPerformanceName] = undefined;
};

export const startLogPerformanceTime = (logPerformanceName: string): void => {
  logPerformanceTimes[logPerformanceName] = Date.now();
};

export const logPerformanceWithStartTime = (
  page: string,
  logPerformanceName: string,
): void => {
  const savedTime = logPerformanceTimes[logPerformanceName];
  if (!savedTime) {
    logger.info(
      `CPL_LOG_PERFORMANCE_WITH_START_TIME_NOT_FOUND_${logPerformanceName}`,
    );
  }

  const timeToInteract = Date.now() - savedTime;

  logger.cpl.logPerformance({
    action: `${page}_page_loaded`,
    page: `main:xo:lite:weasley:guestAndSignup:custom-${page}`,
    timeToInteract,
    uicomp: `custom-${page}`,
    uitype: 'page',
  });

  triggerCPLLatencyMetricsFPTI(page, timeToInteract, logPerformanceName);
};
